import { Fragment, useState, useMemo, useEffect, useRef } from "react";
import { useApi } from "@/hooks/useApi";
import { apiGet, apiSend } from "@/lib/api";
import { toast } from "@/components/ui/feedback";

// 讲解音频管理页 —— 与后端 audio_overview.go 配对。
// 业务规则约束（preset_phrases.go::presetVariantVoice）：
//   concise → 小船长 / story → 金梅导游 / talky → 鲟侠船长
// 三人格不混用 voice；矩阵就绪率/补全均以此为唯一映射。

// 与后端 trackLangs 一一对应（11 种）。
const LANGS: { code: string; label: string }[] = [
  { code: "zh", label: "中文" },
  { code: "en", label: "英语" },
  { code: "ja", label: "日语" },
  { code: "ko", label: "韩语" },
  { code: "ru", label: "俄语" },
  { code: "fr", label: "法语" },
  { code: "es", label: "西语" },
  { code: "de", label: "德语" },
  { code: "it", label: "意语" },
  { code: "pt", label: "葡语" },
  { code: "ar", label: "阿语" },
];
const labelOfLang = (code: string) => LANGS.find((l) => l.code === code)?.label || code;

// 段位 part 默认展示 —— content_N 显示为 "第 N 段"，prompt_N 显示为 "问答 N"，老 segment_X 保留原文。
function prettyPart(part: string): string {
  const m = part.match(/^content_(\d+)$/);
  if (m) return `第 ${m[1]} 段`;
  const p = part.match(/^prompt_(\d+)$/);
  if (p) return `问答 ${p[1]}`;
  const s = part.match(/^segment_(\d+)$/);
  if (s) return `第 ${s[1]} 段`;
  return part;
}

const VARIANT_LABEL: Record<string, string> = { concise: "简约", story: "故事", talky: "话痨" };

interface AudioStats {
  totalScenes: number;
  scenesWithScript: number;
  segments: number;
  langsActive: string[];
  tracksExpected: number;
  tracksReady: number;
  tracksFailed: number;
  tracksGenerating: number;
  tracksStale: number;
  tracksMissing: number;
}

interface SceneRow {
  id: string;
  sceneName: string;
  wordCount: number;
  expected: number;
  ready: number;
  failed: number;
  generating: number;
  stale: number;
  missing: number;
  updatedAt: string;
}

interface TrackRow {
  trackId: string;
  sceneId: string;
  sceneName: string;
  part: string;
  variant: string;
  voice: string;
  lang: string;
  langLabel: string;
  status: "ready" | "failed" | "generating" | "stale" | "missing";
  error: string;
  durationSec: number;
  audioUrl: string;
  updatedAt: string;
}

type Tab = "all" | "generating" | "failed" | "stale" | "missing";

const TAB_LABEL: Record<Tab, string> = {
  all: "全部音频",
  generating: "生成中",
  failed: "已失败",
  stale: "已过期",
  missing: "未生成",
};
const TAB_BADGE: Record<Tab, string> = {
  all: "bg-gray-100 text-gray-700",
  generating: "bg-amber-100 text-amber-700",
  failed: "bg-red-100 text-red-700",
  stale: "bg-orange-100 text-orange-700",
  missing: "bg-gray-100 text-gray-500",
};

// ─────────────────────────────────────────────────────────────
// "全部音频" tab —— scene 列表 + 展开后可试听
// ─────────────────────────────────────────────────────────────

function SceneList({ items, refresh }: { items: SceneRow[]; refresh: () => void }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [trackCache, setTrackCache] = useState<Record<string, TrackRow[]>>({});
  const playerRef = useRef<HTMLAudioElement | null>(null);
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  useEffect(() => () => playerRef.current?.pause(), []);

  const filtered = useMemo(
    () => items.filter((i) => searchQuery === "" || i.sceneName.includes(searchQuery)),
    [items, searchQuery],
  );

  const toggleExpand = async (item: SceneRow) => {
    if (expandedId === item.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(item.id);
    if (!trackCache[item.id]) {
      try {
        const ts = await apiGet<TrackRow[]>(`/audio/tracks?scene=${item.id}`);
        setTrackCache((p) => ({ ...p, [item.id]: ts || [] }));
      } catch {
        /* keep empty */
      }
    }
  };

  const playTrack = (t: TrackRow) => {
    const key = `${t.sceneId}|${t.part}|${t.variant}|${t.lang}`;
    if (playingKey === key) {
      playerRef.current?.pause();
      setPlayingKey(null);
      return;
    }
    playerRef.current?.pause();
    const a = new Audio(t.audioUrl);
    playerRef.current = a;
    a.onended = () => setPlayingKey(null);
    a.onerror = () => setPlayingKey(null);
    a.play().catch(() => setPlayingKey(null));
    setPlayingKey(key);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <span className="text-sm text-gray-500">共 {filtered.length} 个景点</span>
        <div className="relative w-64">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索景点名称"
            className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">景点</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">讲解词字数</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">音轨进度</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">更新时间</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map((item) => {
              const pct = item.expected > 0 ? Math.round((item.ready / item.expected) * 100) : 0;
              return (
                <Fragment key={item.id}>
                  <tr onClick={() => toggleExpand(item)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">
                        <i className={`mr-1 text-gray-400 ${expandedId === item.id ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"}`} />
                        {item.sceneName}
                      </span>
                    </td>
                    <td className="px-4 py-3"><span className="text-sm text-gray-700">{item.wordCount} 字</span></td>
                    <td className="px-4 py-3 min-w-56">
                      {item.expected === 0 ? (
                        <span className="text-xs text-gray-400">讲解词为空</span>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="w-28 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                            <div className={`h-full rounded-full ${pct === 100 ? "bg-emerald-500" : "bg-teal-500"}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className={`text-xs font-medium ${pct === 100 ? "text-emerald-600" : "text-gray-600"}`}>
                            {item.ready}/{item.expected}
                          </span>
                          {item.generating > 0 && (
                            <span className="text-[11px] text-amber-600"><i className="ri-loader-4-line animate-spin" /> {item.generating}</span>
                          )}
                          {item.failed > 0 && <span className="text-[11px] text-red-500">失败 {item.failed}</span>}
                          {item.stale > 0 && <span className="text-[11px] text-orange-500">过期 {item.stale}</span>}
                          {item.missing > 0 && <span className="text-[11px] text-gray-500">缺 {item.missing}</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3"><span className="text-sm text-gray-500">{item.updatedAt}</span></td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <a href={`/knowledge/scenes/${item.id}`} className="w-7 h-7 inline-flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors" title="景点详情">
                        <i className="ri-external-link-line" />
                      </a>
                    </td>
                  </tr>
                  {expandedId === item.id && (
                    <tr key={`${item.id}-grid`} className="bg-teal-50/40">
                      <td colSpan={5} className="px-6 py-4">
                        <SceneTracksGrid tracks={trackCache[item.id]} playingKey={playingKey} onPlay={playTrack} refresh={refresh} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// 展开后：每个段位一行，按 5 lang 排开（每段 1 voice = variant 映射）。
function SceneTracksGrid({
  tracks,
  playingKey,
  onPlay,
  refresh,
}: {
  tracks?: TrackRow[];
  playingKey: string | null;
  onPlay: (t: TrackRow) => void;
  refresh: () => void;
}) {
  if (!tracks) return <div className="text-sm text-gray-400 py-2"><i className="ri-loader-4-line animate-spin mr-1" />加载音轨…</div>;
  if (!tracks.length) return <div className="text-sm text-gray-400 py-2">该景点暂无讲解词或音轨</div>;
  // (part, variant) 分组
  const groups = new Map<string, TrackRow[]>();
  for (const t of tracks) {
    const k = `${t.variant}|${t.part}`;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(t);
  }
  const groupKeys = Array.from(groups.keys()).sort((a, b) => {
    const [va, pa] = a.split("|");
    const [vb, pb] = b.split("|");
    if (va !== vb) return va.localeCompare(vb);
    return pa.localeCompare(pb);
  });

  const regenOne = async (t: TrackRow) => {
    try {
      await apiSend("POST", "/audio/tracks/regenerate", {
        targets: [{ sceneId: t.sceneId, part: t.part, variant: t.variant, voice: t.voice, lang: t.lang }],
      });
      toast("已加入生成队列", "success");
      refresh();
    } catch (e) {
      toast("生成失败：" + (e as Error).message, "error");
    }
  };

  return (
    <div className="space-y-2.5">
      {groupKeys.map((gk) => {
        const rows = groups.get(gk)!;
        const head = rows[0];
        return (
          <div key={gk} className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-gray-700 w-32 shrink-0">
              {prettyPart(head.part)} · {VARIANT_LABEL[head.variant] || head.variant} · {head.voice}
            </span>
            {rows.map((t) => {
              const key = `${t.sceneId}|${t.part}|${t.variant}|${t.lang}`;
              const active = playingKey === key;
              const canPlay = t.status === "ready" && t.audioUrl;
              const tone =
                t.status === "ready" ? (active ? "bg-teal-600 text-white" : "bg-white border border-teal-200 text-teal-700 hover:bg-teal-50") :
                t.status === "stale" ? "bg-orange-100 text-orange-700" :
                t.status === "failed" ? "bg-red-100 text-red-700" :
                t.status === "generating" ? "bg-amber-100 text-amber-700" :
                "bg-gray-100 text-gray-400";
              return (
                <span key={t.lang} className="inline-flex items-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (canPlay) onPlay(t);
                    }}
                    disabled={!canPlay}
                    title={t.error || t.status}
                    className={`px-2.5 py-1 rounded-l-full text-xs font-medium flex items-center gap-1 transition-colors ${tone} ${canPlay ? "" : "cursor-default"}`}
                  >
                    {canPlay && <i className={active ? "ri-volume-up-line" : "ri-play-mini-fill"} />}
                    {t.langLabel}{t.durationSec ? ` ${t.durationSec}s` : ""}
                  </button>
                  {t.status !== "ready" && t.status !== "generating" && (
                    <button
                      onClick={(e) => { e.stopPropagation(); regenOne(t); }}
                      className="px-1.5 py-1 rounded-r-full text-xs bg-white border border-teal-200 text-teal-700 hover:bg-teal-50"
                      title="重新生成"
                    >
                      <i className="ri-refresh-line" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 非 "全部" tab —— track 明细表，带勾选 + 批量重生成
// ─────────────────────────────────────────────────────────────

function TrackList({
  tab,
  tracks,
  loading,
  refresh,
}: {
  tab: Tab;
  tracks: TrackRow[];
  loading: boolean;
  refresh: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const playerRef = useRef<HTMLAudioElement | null>(null);
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  useEffect(() => () => playerRef.current?.pause(), []);
  useEffect(() => { setSelected(new Set()); }, [tab]);

  const filtered = useMemo(
    () => tracks.filter((t) => searchQuery === "" || t.sceneName.includes(searchQuery)),
    [tracks, searchQuery],
  );

  const keyOf = (t: TrackRow) => `${t.sceneId}|${t.part}|${t.variant}|${t.voice}|${t.lang}`;
  const canRegen = tab === "failed" || tab === "stale" || tab === "missing";

  const toggleOne = (t: TrackRow) => {
    setSelected((p) => {
      const n = new Set(p);
      const k = keyOf(t);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected((p) => {
      if (p.size === filtered.length) return new Set();
      return new Set(filtered.map(keyOf));
    });
  };

  const playTrack = (t: TrackRow) => {
    const k = keyOf(t);
    if (playingKey === k) { playerRef.current?.pause(); setPlayingKey(null); return; }
    playerRef.current?.pause();
    const a = new Audio(t.audioUrl);
    playerRef.current = a;
    a.onended = () => setPlayingKey(null);
    a.onerror = () => setPlayingKey(null);
    a.play().catch(() => setPlayingKey(null));
    setPlayingKey(k);
  };

  const regenerateSelected = async () => {
    const targets = filtered
      .filter((t) => selected.has(keyOf(t)))
      .map((t) => ({ sceneId: t.sceneId, part: t.part, variant: t.variant, voice: t.voice, lang: t.lang }));
    if (!targets.length) return;
    if (!window.confirm(`将重新生成 ${targets.length} 条音轨（含翻译+TTS 计费）。确定继续？`)) return;
    setBusy(true);
    try {
      const r = await apiSend<{ queued: number }>("POST", "/audio/tracks/regenerate", { targets });
      toast(`已排队 ${r?.queued ?? targets.length} 条音轨`, "success");
      setSelected(new Set());
      refresh();
    } catch (e) {
      toast("重新生成失败：" + (e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">共 {filtered.length} 条音轨</span>
          {canRegen && selected.size > 0 && (
            <span className="text-xs text-teal-600 font-medium">已选 {selected.size}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-56">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索景点名称"
              className="w-full pl-9 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>
          {canRegen && (
            <button
              onClick={regenerateSelected}
              disabled={busy || selected.size === 0}
              className="px-3 py-1.5 bg-teal-600 text-white rounded-md text-xs font-medium hover:bg-teal-700 disabled:opacity-50 whitespace-nowrap"
            >
              <i className={busy ? "ri-loader-4-line animate-spin mr-1" : "ri-refresh-line mr-1"} />
              重新生成所选
            </button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {canRegen && (
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleAll}
                    className="rounded text-teal-600 focus:ring-teal-500"
                  />
                </th>
              )}
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">景点</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">段位 · 风格</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">角色</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">语言</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">更新时间</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading && (
              <tr><td colSpan={canRegen ? 8 : 7} className="px-4 py-6 text-center text-sm text-gray-400">
                <i className="ri-loader-4-line animate-spin mr-1" />加载中…
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={canRegen ? 8 : 7} className="px-4 py-6 text-center text-sm text-gray-400">无相关音轨</td></tr>
            )}
            {filtered.map((t) => {
              const k = keyOf(t);
              const active = playingKey === k;
              const badge =
                t.status === "ready" ? "bg-emerald-100 text-emerald-700" :
                t.status === "failed" ? "bg-red-100 text-red-700" :
                t.status === "generating" ? "bg-amber-100 text-amber-700" :
                t.status === "stale" ? "bg-orange-100 text-orange-700" :
                "bg-gray-100 text-gray-500";
              return (
                <tr key={k} className="hover:bg-gray-50">
                  {canRegen && (
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={selected.has(k)}
                        onChange={() => toggleOne(t)}
                        className="rounded text-teal-600 focus:ring-teal-500"
                      />
                    </td>
                  )}
                  <td className="px-3 py-2.5 text-sm text-gray-900 whitespace-nowrap">{t.sceneName}</td>
                  <td className="px-3 py-2.5 text-sm text-gray-600 whitespace-nowrap">
                    {prettyPart(t.part)} · {VARIANT_LABEL[t.variant] || t.variant}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-gray-600 whitespace-nowrap">{t.voice}</td>
                  <td className="px-3 py-2.5 text-sm text-gray-600 whitespace-nowrap">{t.langLabel}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge}`} title={t.error}>
                      {t.status === "generating" && <i className="ri-loader-4-line animate-spin" />}
                      {t.status === "ready" ? "已就绪"
                        : t.status === "failed" ? "失败"
                        : t.status === "stale" ? "已过期"
                        : t.status === "generating" ? "生成中"
                        : "未生成"}
                    </span>
                    {t.error && (
                      <div className="text-[11px] text-red-500 mt-0.5 max-w-md truncate" title={t.error}>{t.error}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-gray-500 whitespace-nowrap">{t.updatedAt || "--"}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      {t.audioUrl && (
                        <button
                          onClick={() => playTrack(t)}
                          className={`w-7 h-7 inline-flex items-center justify-center rounded ${active ? "bg-teal-100 text-teal-600" : "text-gray-400 hover:text-teal-600 hover:bg-teal-50"}`}
                          title="试听旧版本"
                        >
                          <i className={active ? "ri-volume-up-line" : "ri-play-line"} />
                        </button>
                      )}
                      <a
                        href={`/knowledge/scenes/${t.sceneId}`}
                        className="w-7 h-7 inline-flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded"
                        title="景点详情"
                      >
                        <i className="ri-external-link-line" />
                      </a>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 多语言内容批量（保留旧逻辑，与本页矩阵互补）
// ─────────────────────────────────────────────────────────────

interface JobStatus { running: boolean; total: number; done: number; skipped: number; failed: number }
function ContentI18nPanel() {
  const [trans, setTrans] = useState<JobStatus | null>(null);
  const [audio, setAudio] = useState<JobStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const refresh = async () => {
    try { setTrans(await apiGet<JobStatus>("/content/translate/status")); } catch { /* offline */ }
    try { setAudio(await apiGet<JobStatus>("/content/audio/generate-all/status")); } catch { /* offline */ }
  };
  useEffect(() => {
    refresh();
    pollRef.current = setInterval(refresh, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);
  const startTranslate = async () => {
    try { await apiSend("POST", "/content/translate", {}); toast("已开始翻译展示文本(全部语言·差量)", "success"); refresh(); }
    catch { toast("启动失败", "error"); }
  };
  const startAudio = async () => {
    if (!window.confirm("将为全部景点生成多语言讲解语音(TTS 开销较大,已生成且未变的会跳过)。确定开始?")) return;
    try { await apiSend("POST", "/content/audio/generate-all", {}); toast("已开始生成多语言讲解语音", "success"); refresh(); }
    catch { toast("启动失败", "error"); }
  };
  const stat = (s: JobStatus | null) =>
    s?.running ? `进行中 ${s.done}/${s.total}（跳过 ${s.skipped}・失败 ${s.failed}）`
      : s && (s.done || s.skipped || s.failed) ? `空闲（上次 完成 ${s.done}・跳过 ${s.skipped}・失败 ${s.failed}）`
        : "空闲";
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-900"><i className="ri-translate-2 mr-1.5" />多语言内容（一键差量）</div>
        <div className="text-xs text-gray-500 mt-0.5">展示文本(景点/游线 名称·描述)+ 讲解语音批量翻译/生成；只重做有变化的，App 打开自动同步。</div>
      </div>
      <div className="flex flex-col gap-1.5 sm:min-w-[280px]">
        <div className="flex items-center gap-2">
          <button onClick={startTranslate} disabled={trans?.running} className="px-3 py-1.5 bg-teal-600 text-white rounded-md text-xs font-medium hover:bg-teal-700 disabled:opacity-60 whitespace-nowrap">
            <i className={trans?.running ? "ri-loader-4-line animate-spin mr-1" : "ri-translate mr-1"} />翻译展示文本
          </button>
          <span className="text-xs text-gray-500 truncate">{stat(trans)}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={startAudio} disabled={audio?.running} className="px-3 py-1.5 bg-indigo-600 text-white rounded-md text-xs font-medium hover:bg-indigo-700 disabled:opacity-60 whitespace-nowrap">
            <i className={audio?.running ? "ri-loader-4-line animate-spin mr-1" : "ri-mic-2-line mr-1"} />生成多语言语音
          </button>
          <span className="text-xs text-gray-500 truncate">{stat(audio)}</span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 顶层页面
// ─────────────────────────────────────────────────────────────

function AudioPage() {
  const [tab, setTab] = useState<Tab>("all");
  const [batchBusy, setBatchBusy] = useState(false);

  const { data: stats, reload: reloadStats } = useApi<AudioStats>("/audio/stats", {
    totalScenes: 0, scenesWithScript: 0, segments: 0, langsActive: [],
    tracksExpected: 0, tracksReady: 0, tracksFailed: 0, tracksGenerating: 0, tracksStale: 0, tracksMissing: 0,
  });
  const { data: scenes, reload: reloadScenes } = useApi<SceneRow[]>("/audio", []);
  // 非 all tab 才真正用 tracks，但 useApi 始终生效；切到 all 时数据会被忽略。
  const trackStatus = tab === "all" ? "failed" : tab;
  const { data: tracks, loading: tracksLoading, reload: reloadTracks } = useApi<TrackRow[]>(
    `/audio/tracks?status=${trackStatus}`, [],
  );

  const refresh = () => { reloadStats(); reloadScenes(); reloadTracks(); };

  // 后台有 generating/failed 时自动轮询
  useEffect(() => {
    if (stats.tracksGenerating <= 0) return;
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats.tracksGenerating]);

  const batchGenerate = async () => {
    if (!window.confirm("将为全部缺失/失败/过期的音轨补全。确定继续？")) return;
    setBatchBusy(true);
    try {
      const r = await apiSend<{ queued: number }>("POST", "/audio/generate-missing", {});
      toast(r?.queued ? `已排队 ${r.queued} 条音轨，后台生成中` : "没有需要补全的音轨", r?.queued ? "success" : "info");
      refresh();
    } catch (e) { toast("批量生成失败：" + (e as Error).message, "error"); }
    finally { setBatchBusy(false); }
  };

  const tabs: { key: Tab; count: number }[] = [
    { key: "all", count: stats.tracksExpected },
    { key: "generating", count: stats.tracksGenerating },
    { key: "failed", count: stats.tracksFailed },
    { key: "stale", count: stats.tracksStale },
    { key: "missing", count: stats.tracksMissing },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">讲解音频管理</h1>
          <p className="text-sm text-gray-500 mt-1">
            按段位 × 角色（concise=小船长 / story=金梅 / talky=鲟侠船长）× {stats.langsActive.length || "?"} 语言（{stats.langsActive.map(labelOfLang).join("/") || "—"}）矩阵管理
          </p>
        </div>
        <button onClick={batchGenerate} disabled={batchBusy} className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors shadow-sm whitespace-nowrap disabled:opacity-60">
          <i className={batchBusy ? "ri-loader-4-line animate-spin mr-1.5" : "ri-magic-line mr-1.5"} />补全缺失音轨
        </button>
      </div>

      <ContentI18nPanel />

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: "总景点", value: `${stats.totalScenes}`, cls: "border-gray-200 text-gray-900" },
          { label: "应有音轨", value: `${stats.tracksExpected}`, cls: "border-teal-200 text-teal-700 bg-teal-50" },
          { label: "已就绪", value: `${stats.tracksReady}`, cls: "border-emerald-200 text-emerald-700 bg-emerald-50" },
          { label: "生成中", value: `${stats.tracksGenerating}`, cls: "border-amber-200 text-amber-700 bg-amber-50" },
          { label: "已失败", value: `${stats.tracksFailed}`, cls: "border-red-200 text-red-700 bg-red-50" },
          { label: "已过期", value: `${stats.tracksStale}`, cls: "border-orange-200 text-orange-700 bg-orange-50" },
        ].map((c) => (
          <div key={c.label} className={`border rounded-lg p-4 ${c.cls}`}>
            <div className="text-xs text-gray-500 mb-1">{c.label}</div>
            <div className="text-2xl font-bold">{c.value}</div>
          </div>
        ))}
      </div>

      {/* tab 切换 */}
      <div className="flex items-center gap-2 flex-wrap border-b border-gray-200">
        {tabs.map(({ key, count }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key ? "border-teal-600 text-teal-700" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {TAB_LABEL[key]}
            <span className={`ml-1.5 inline-flex items-center justify-center min-w-6 h-5 px-1.5 rounded-full text-[11px] font-medium ${TAB_BADGE[key]}`}>
              {count}
            </span>
          </button>
        ))}
      </div>

      {tab === "all" ? (
        <SceneList items={scenes} refresh={refresh} />
      ) : (
        <TrackList tab={tab} tracks={tracks} loading={tracksLoading} refresh={refresh} />
      )}
    </div>
  );
}

export default AudioPage;
